/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useSelector } from "react-redux";

import { ACCOUNT_ROUTES } from "../../../features/account/constants";
import { selectCurrentFarmerAccount } from "../../../features/azure-b2c/storage";
import { CARD_PARTNER_DIRECTORY_ROUTES } from "../../../features/card-partner-directory/constants";
import { ORDERS_ROUTES } from "../../../features/orders/constants";
import { PRODUCTS_ROUTES } from "../../../features/products/constants";
import { QUOTES_ROUTES } from "../../../features/quotes/constants";
import { AGREEMENTS_ROUTES } from "../../../features/sales-agreements/constants";
import { SEED_ROUTES } from "../../../features/seed-orders/constants";
import { usePermission } from "../../../hooks/usePermission";
import { selectRemoteConfig } from "../../../services/firebase/remote-config/remoteConfig.slice";
import { ScrollYPositionKeys } from "../../../util/consts";
import { HOME_ROUTE } from "../../../util/routing";

export type NavDestinationId =
  "home" | "shop" | "agreements" | "cardPartners" | "seed" | "quotes" | "orders" | "account";

export type NavDestination = {
  to: string;
  visible: boolean;
  scrollYKey?: ScrollYPositionKeys;
};

/**
 * Where each nav destination routes to and whether the current user can see it.
 * Surface-agnostic — no icon, label, or layout concerns live here. Each nav
 * surface (side, bottom) decides independently which destinations to show and how.
 */
export const useNavDestinations = (): Record<NavDestinationId, NavDestination> => {
  const remoteConfig = useSelector(selectRemoteConfig);
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const hasAccess = !!(currentAccount?.hasShareholderAccess || currentAccount?.hasTfoAccess);

  const canViewSalesAgreements = usePermission("SalesAgreements_AgreementView").allowed;
  const canCreateSeedOrder = usePermission("Orders_SeedOrderCreate").allowed;
  const canViewOrders = usePermission("Orders_OrderView").allowed;
  const canViewQuotes = usePermission("Quotes_QuoteView").allowed;
  const canViewProShop = usePermission("FarmlandsProShop_ShopView").allowed;

  return {
    home: { to: HOME_ROUTE, visible: true },
    shop: {
      to: PRODUCTS_ROUTES.BASE,
      visible: hasAccess && canViewProShop,
      scrollYKey: ScrollYPositionKeys.Products,
    },
    agreements: {
      to: AGREEMENTS_ROUTES.BASE,
      visible: hasAccess && canViewSalesAgreements,
      scrollYKey: ScrollYPositionKeys.Agreements,
    },
    cardPartners: { to: CARD_PARTNER_DIRECTORY_ROUTES.BASE, visible: hasAccess },
    seed: {
      to: SEED_ROUTES.BASE,
      visible: hasAccess && remoteConfig.seedEnabled && canCreateSeedOrder,
      scrollYKey: ScrollYPositionKeys.Seeds,
    },
    quotes: {
      to: QUOTES_ROUTES.BASE,
      visible: hasAccess && remoteConfig.quoteEnabled && canViewQuotes,
    },
    orders: {
      to: ORDERS_ROUTES.BASE,
      visible: hasAccess && canViewOrders,
      scrollYKey: ScrollYPositionKeys.Orders,
    },
    account: { to: ACCOUNT_ROUTES.BASE, visible: true },
  };
};
